import { SUPPORTED_CURRENCIES, getCountryForCurrency } from '@bbpos/types';
import { storeTimezone } from '@/lib/store-clock';
import { useAuthStore } from '@/stores/use-auth-store';

// Presentation helpers for the Customer CRM.
//
// Phones are stored in strict E.164 (CLAUDE.md) but displayed in a human
// friendly form. India keeps its own 5+5 grouping; every other country is
// grouped generically rather than printed as one undivided digit run.

/**
 * National-number length per supported dial code, for the length check in
 * {@link toE164}. Keyed by dial code rather than by country because the number
 * carries its own country code — nothing has to be threaded in. Unlisted codes
 * fall back to the generic E.164 bounds.
 *
 * Built from the shared country table rather than hand-listed. It used to be a
 * second copy of the same seven numbers, which is exactly the kind of pair that
 * drifts: one of them gets a correction and the other does not.
 */
const NATIONAL_DIGITS: Record<string, number> = Object.fromEntries(
  SUPPORTED_CURRENCIES.map((currency) => {
    const country = getCountryForCurrency(currency);
    return [country.dial, Math.max(...country.nationalDigits)];
  }),
);

// Longest-first, so '+968' is not read as '+96' or '+9'.
const DIAL_CODES = Object.keys(NATIONAL_DIGITS).sort((a, b) => b.length - a.length);

/**
 * Display a phone number with a country-code-aware spacing.
 *
 *   "+919876543210"  → "+91 98765 43210"
 *   "+96812345678"   → "+968 1234 5678"
 *   "9876543210"     → "+91 98765 43210" (auto-prepends IN code)
 *   ""               → "—"
 */
export function formatPhone(raw: string | undefined | null): string {
  if (!raw) return '—';
  const trimmed = raw.trim();
  if (!trimmed) return '—';

  // Bare 10-digit Indian number — auto-prefix
  const digits = trimmed.replace(/\D/g, '');
  if (!trimmed.startsWith('+') && digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }

  if (trimmed.startsWith('+91') && digits.length >= 12) {
    const rest = digits.slice(2); // drop country code from the digit string
    if (rest.length === 10) {
      return `+91 ${rest.slice(0, 5)} ${rest.slice(5)}`;
    }
  }

  // Every other country: split off the dial code and group the national part in
  // fours. Previously these fell straight through and rendered as one unbroken
  // run of digits — the same shape @bbpos/utils' formatPhone already avoids.
  const generic = trimmed.match(/^\+(\d{1,3})(\d{7,14})$/);
  if (generic) {
    const [, cc, rest] = generic;
    return `+${cc} ${rest.replace(/(\d{4})(?=\d)/g, '$1 ').trim()}`;
  }

  return trimmed;
}

/**
 * Accept a raw input and return a canonical E.164 phone if possible, else
 * null. Used by the Create form to normalise before submit.
 */
export function toE164(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  // Raw 10-digit → assume India
  if (!raw.startsWith('+') && digits.length === 10) {
    return `+91${digits}`;
  }
  // Already +prefixed. The `variant="phone"` Input prepends the picked dial
  // code to whatever is typed, so typing "12345" arrives here as "+9112345" —
  // 7 digits, which satisfies E.164's generic `\d{6,14}` and would be stored
  // as a real customer phone. E.164 alone cannot catch that, so a number is
  // additionally held to its country's national length.
  //
  // That guard used to apply to India only. Once the picker started offering
  // GCC countries the same typo became possible there and went unchecked —
  // "12345" at an Omani tenant arrives as "+96812345", 8 digits, accepted.
  if (raw.trim().startsWith('+')) {
    const candidate = `+${digits}`;
    if (!isValidE164(candidate)) return null;
    const dial = DIAL_CODES.find((d) => candidate.startsWith(d));
    if (dial && candidate.length !== dial.length + NATIONAL_DIGITS[dial]) {
      return null;
    }
    return candidate;
  }
  return null;
}

/**
 * Strict E.164 validator from CLAUDE.md.
 */
const E164_REGEX = /^\+[1-9]\d{6,14}$/;
export function isValidE164(value: string): boolean {
  return E164_REGEX.test(value);
}

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export function isValidGstin(value: string): boolean {
  if (!value) return true; // optional field
  return GSTIN_REGEX.test(value.trim().toUpperCase());
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(value: string): boolean {
  if (!value) return true; // optional field
  return EMAIL_REGEX.test(value.trim());
}

/** The signed-in business's zone — the default clock for the helpers below (CU2-011). */
function businessTimezone(): string {
  return storeTimezone(useAuthStore.getState().profile);
}

/**
 * Display a timestamp in the Orio360 admin short format: "05 Apr 2026".
 * Uses en-IN locale so month names match the rest of the app. The day is the
 * store's (`timeZone`, by default the business's), never this browser's.
 */
export function formatShortDate(
  input: string | Date | undefined | null,
  timeZone: string = businessTimezone(),
): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone });
}

/**
 * Display a timestamp with time: "05 Apr 2026, 14:32" — on `timeZone`'s clock,
 * by default the business's: an IST laptop must show a Muscat store's times as
 * the till does (CU2-011).
 */
export function formatDateTime(
  input: string | Date | undefined | null,
  timeZone: string = businessTimezone(),
): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone })}, ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone })}`;
}

/**
 * Short initials used by <Avatar> for customers without a photo.
 */
export function customerInitials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
